/* Copyright (c) 2020-present CloudIO Inc */

import {
  Attribute,
  DataRecord,
  RecordStatus,
  Row,
} from '@cloudio-saas/datasource-types';
import {
  emptyFunction,
  Path,
  selector,
  useLatest,
  useStateX,
  useStateXGetter,
  useStateXSelector,
  useStateXSetter,
  useStateXValue,
} from '@cloudio-saas/statex';
import { RowIndexContext, useRowIndex } from 'common/Context';
import type {
  BaseProps,
  BooleanExpressionValue,
  DataStoreReference,
} from 'componentregistry';
import { useBooleanExpression } from 'expression/useExpression';
import React, {
  createContext,
  CSSProperties,
  ReactNode,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import SimpleLoading from 'root/SimpleLoading';
import Store, {
  EMPTY_ARRAY,
  EMPTY_OBJECT,
  MISSING_STORE_PATH,
} from 'store/Store';
import {
  useStoreCurrentRecordIdCallback,
  useStoreAttributeSortState,
  useStoreBusy,
  useStoreDirty,
  useStoreInsertable,
  useStoreUpdatable,
} from 'store/useAppStore';
import { isEmpty, isNull, isNullEqual } from 'utils/isEmpty';
import { isFromDB } from '../../store/storeTypes';

const StoreContext = createContext<Store<unknown> | null>(null);

interface Props
  extends Required<DataStoreReference.DataStoreReference>,
    BaseProps<'DataStoreReference'> {}

function IODataStoreReference(
  props: Props,
  ref: React.Ref<HTMLDivElement>,
): React.ReactElement {
  const {
    children,
    className,
    alias,
    domProps,
    platform,
    pageId,
    width,
    height,
    appUid,
  } = props;
  const store = alias ? platform.getStore(appUid, pageId, alias) ?? null : null;
  let style: CSSProperties = { position: 'relative' };
  style = { ...style, width: width ?? '100%', height };
  return (
    <StoreContext.Provider value={store}>
      <div className={className} ref={ref} {...domProps} style={style}>
        <Suspense fallback={<SimpleLoading name="DataStoreReference" />}>
          {React.Children.map<ReactNode, ReactNode>(children, (child) => {
            if (React.isValidElement<BaseProps<never>>(child)) {
              return React.cloneElement(child, { width, height });
            }
            return child;
          })}
        </Suspense>
      </div>
    </StoreContext.Provider>
  );
}

function useCurrentDataStore<S>() {
  return useContext(StoreContext) as Store<S> | null;
}

/**
 * Use this hook if you want to rerender when any of the store record changes
 * @param store The store to watch for
 * @returns the store records
 */
function useStoreRecords<S>(store?: Store<S> | null): Row<S>[] {
  const recordsOrder = useStateXValue<string[]>(
    store?.recordsOrderPath ?? ['dummy', 'recordsOrder'],
    [],
  );
  const records = useStateXValue<Record<string, Row<S>>>(
    store?.recordsPath ?? ['dummy', 'records'],
    {},
  );
  return useMemo(() => {
    return recordsOrder.map((key) => records[key]);
  }, [records, recordsOrder]);
}

/**
 * Use this hook if you want to rerender when any of the parent store record changes
 * @returns the store records
 */
function useRecords<T>(): Row<T>[] {
  const store = useContext(StoreContext);
  return useStoreRecords(store);
}

function useRecordCount(): number {
  const store = useContext(StoreContext);
  const recordsOrder =
    useStateXValue(store?.recordsOrderPath ?? ['x'], undefined) ?? [];
  return recordsOrder.length;
}

function useRecordsFreq(viewAttribute: string) {
  const store = useContext(StoreContext);
  const set = useStateXSetter();
  const get = useStateXGetter();
  const curPath = [...(store?.recordsFreqPath ?? ['x']), viewAttribute];
  const [recordsFreq] = useStateX<Record<string | number, Set<string>>>(
    curPath,
    {},
  );
  const updateFreq = (word: string | number, id: string) => {
    const setOfWords: Set<string> = get([...curPath, `${word}`]) ?? new Set();
    setOfWords.add(id);
    set([...curPath, `${word}`], setOfWords);
  };
  return { recordsFreq, updateFreq };
}

function useDataStoreBusy(): boolean {
  const store = useContext(StoreContext);
  return useStoreBusy(store);
}

function useDataStoreDirty(): boolean {
  const store = useContext(StoreContext);
  return useStoreDirty(store);
}

function useAttributeSortState(attribute = '_dummy_attr'): number {
  return useStoreAttributeSortState(useCurrentDataStore(), attribute);
}

/**
 * Use this hook if you want to rerender when the parent store current (active) record changes
 * @returns the parent store's current record
 */
function useCurrentRecord<T>(): Row<T> | undefined {
  const store = useContext(StoreContext);
  const currentRecordId = useStateXValue(
    store?.currentRecordIdPath ?? ['dummy', 'currentRecord'],
    '',
  );
  const currentRecordPath: Path =
    store && currentRecordId
      ? [...store.recordsPath, currentRecordId]
      : ['dummy', 'record'];
  return useStateXValue<Row<T> | undefined>(currentRecordPath, undefined);
}

export function isDeleted<T>(row?: Row<T> | null): boolean {
  return !isNull(row) && row._rs === 'D';
}

function useIsRecordAtIndexDeleted(rowIndex?: number): boolean {
  const currentRecordId = useRecordId(rowIndex);
  return useIsRecordDeleted(currentRecordId);
}

function useIsRecordDeleted(recordId: string): boolean {
  const store = useContext(StoreContext);
  const id = useLatest(recordId);
  const currentRecordPath: Path =
    store && recordId
      ? [...store.recordsPath, recordId, '_rs']
      : ['dummy', 'record'];
  const [deleted, setDeleted] = useState(() =>
    recordId ? isDeleted(store?.getRecord(recordId)) : false,
  );
  useStateXValue<RecordStatus | undefined>(currentRecordPath, undefined, {
    shouldComponentUpdate: (_rs) => {
      setDeleted(_rs === 'D');
      return false;
    },
  });

  if (id.current !== recordId) {
    // the above shouldComponentUpdate doesn't fire when
    // the currentRecordPath changes, hence this check
    const del = recordId ? isDeleted(store?.getRecord(recordId)) : false;
    if (del !== deleted) {
      setDeleted(del);
    }
  }

  return deleted;
}

function useIsCurrentRecordFromDB(): boolean {
  const store = useContext(StoreContext);
  const currentRecordId = useStateXValue(
    store?.currentRecordIdPath ?? ['dummy', 'currentRecord'],
    '',
  );
  return useIsRecordFromDB(currentRecordId);
}

function useIsRecordAtIndexFromDB(rowIndex?: number): boolean {
  const currentRecordId = useRecordId(rowIndex);
  return useIsRecordFromDB(currentRecordId);
}

function useIsRecordAtIndexCurrent(rowIndex = -1): boolean {
  const store = useContext(StoreContext);
  const currentRecordId = useRecordId(rowIndex);
  const [val, setValue] = useState(
    currentRecordId === store?.getCurrentRecordId(),
  );
  useStoreCurrentRecordIdCallback(store, ({ value }) => {
    // get the latest current record id for this rowIndex
    // currentRecordId returned by useRecordId may not yet updated
    // to the latest value during this callback
    if (rowIndex >= 0 && store && store.size() > rowIndex) {
      const crid = store.getRecordIdAtRow(rowIndex);
      if (val !== (value === crid)) {
        setValue(value === crid);
      }
    }
  });
  return rowIndex >= 0 && rowIndex !== Number.MAX_VALUE && val;
}

function useIsRecordFromDB<T = DataRecord>(currentRecordId: string): boolean {
  const store = useContext(StoreContext);
  const id = useRef(currentRecordId);
  const currentRecordPath: Path =
    store && currentRecordId
      ? [...store.recordsPath, currentRecordId]
      : ['dummy', 'record'];
  const [fromDB, setFromDB] = useState(() =>
    currentRecordId ? isFromDB(store?.getRecord(currentRecordId)) : false,
  );
  useStateXValue<Row<T> | undefined>(currentRecordPath, undefined, {
    shouldComponentUpdate: (_row) => {
      setFromDB(_row ? isFromDB(_row) : false);
      return false;
    },
  });

  useEffect(() => {
    if (id.current !== currentRecordId) {
      id.current = currentRecordId;
      // the above shouldComponentUpdate doesn't fire when
      // the currentRecordPath changes, hence this check
      const fromdb = currentRecordId
        ? isFromDB(store?.getRecord(currentRecordId))
        : false;
      setFromDB(fromdb);
    }
  }, [currentRecordId, store]);

  return fromDB;
}

function useIsEditable({
  appUid,
  insertable,
  itemId,
  pageId,
  updatable,
  rowIndex,
}: {
  appUid: string;
  insertable: BooleanExpressionValue;
  itemId: string;
  pageId: string;
  updatable: BooleanExpressionValue;
  rowIndex?: number;
}): boolean {
  const store = useCurrentDataStore();
  const fromDB = useIsRecordAtIndexFromDB(rowIndex);

  const insertableResult = useBooleanExpression(
    {
      exp: insertable,
      context: {
        appUid,
        pageId,
        itemId,
        property: 'insertable',
        data: {},
        rowIndex,
      },
    },
    true,
  );
  const updatableResult = useBooleanExpression(
    {
      exp: updatable,
      context: {
        appUid,
        pageId,
        itemId,
        property: 'updatable',
        data: {},
        rowIndex,
      },
    },
    true,
  );

  const _insertable = useStoreInsertable(store);
  const _updatable = useStoreUpdatable(store);

  if (store && !(fromDB ? _updatable : _insertable)) {
    // it's not editable at the store level
    return false;
  }

  if (typeof rowIndex === 'number' && rowIndex < 0) {
    return false;
  }
  return fromDB ? updatableResult : insertableResult;
}

function useStoreRecordId(store: Store<any> | null, row?: number): string {
  const rowIndex = useRowIndex();
  row = row ?? rowIndex;
  let path;
  // row will be set to Number.MAX_VALUE for the cells inside header & footer
  if (store && row !== Number.MAX_VALUE) {
    if (row === undefined || row < 0) {
      path = store.currentRecordIdPath;
    } else {
      path = [...store.recordsOrderPath, row];
    }
  } else {
    path = ['dummy', 'recordId'];
  }
  const id = useStateXValue(path, '');
  // console.log('useRecordId', row, id);
  return id;
}

function useRecordId(row?: number) {
  const store = useContext(StoreContext);
  return useStoreRecordId(store, row);
}

const DUMMY_RECORD_PATH = ['$_dummy_$', '$_record_$', '$_attr_$'];

function useStoreAttribute<T, S>(
  store: Store<S> | null,
  recordId: string | null,
  attribute: string,
  defaultVal: T,
): [T, React.Dispatch<React.SetStateAction<T>>, string] {
  const attributePath: Path =
    store && recordId && attribute
      ? [...store.recordsPath, recordId, attribute]
      : DUMMY_RECORD_PATH;
  const [value, setAttrValue] = useStateX<T>(attributePath, defaultVal);
  const get = useStateXGetter();
  const set = useStateXSetter();
  const setValue = useCallback<React.Dispatch<React.SetStateAction<T>>>(
    (ssa: React.SetStateAction<T>) => {
      if (!(store && recordId) || isEmpty(attribute)) return;
      let val: unknown;
      if (typeof ssa === 'function') {
        // @ts-ignore ssa is a function
        val = ssa(get([...store.recordsPath, recordId, attribute]));
      } else {
        val = ssa;
      }
      // @ts-ignore Partial<S>
      const updatedRecord = store.processUpdateRecord(recordId, {
        [attribute]: val,
      });
      // update the attribute to avoid cursor jump issue
      setAttrValue(updatedRecord[attribute as keyof S] as unknown as T);
      // and then update the record for the rest
      set([...store.recordsPath, recordId], updatedRecord);
    },
    [attribute, get, recordId, set, setAttrValue, store],
  );
  if (DUMMY_RECORD_PATH === attributePath || !recordId) {
    // if no attribute defined, then always return defaultVal & emptyFunction to prevent any editing
    return [defaultVal, emptyFunction, recordId ?? '_MISSING_RECORD_ID_'];
  }
  return [value, setValue, recordId];
}

function useStoreCurrentRecordAttribute<T>(
  store: Store<DataRecord> | null,
  attribute: string,
  defaultVal: T,
): [T, React.Dispatch<React.SetStateAction<T>>, string] {
  const recordId = useStateXValue(
    store?.currentRecordIdPath ?? MISSING_STORE_PATH,
    null,
  );
  return useStoreAttribute(store, recordId, attribute, defaultVal);
}

/**
 * This hook is similar to react's useState hook, but uses the global
 * state of the given attribute
 *
 * @example
 * ```
 * const [value, setValue] = useAttribute(viewAttribute, defaultValue);
 * ```
 * @param attribute the store attribute
 * @param row an optional row index
 * @returns [value, setValue, recordId]
 */
function useAttribute<T>(
  attribute: string,
  defaultVal: T,
  row?: number,
): [T, React.Dispatch<React.SetStateAction<T>>, string] {
  const store = useContext(StoreContext);
  const recordId = useRecordId(row);
  return useStoreAttribute(store, recordId, attribute, defaultVal);
}

function useAttributeDefinition(attribute: string): Attribute | undefined {
  const store = useContext(StoreContext);
  return store?.getAttribute(attribute);
}

function useSetCurrentRecord() {
  const store = useContext(StoreContext);
  const get = useStateXGetter();
  const recordIndex = useContext(RowIndexContext);
  const setCurrentRecord = useCallback(
    (row: number | undefined | null) => {
      if (!store) return;
      row = row ?? recordIndex;
      if (isNull(row)) return;
      const targetRow = row;
      if (targetRow < 0) {
        // header click
        return;
      }
      const totalRecords = store.size();
      if (targetRow >= totalRecords) {
        // footer click
        return;
      }
      const id = get<string>([...store.recordsOrderPath, targetRow]);
      store.setCurrentRecordId(id);
    },
    [get, store, recordIndex],
  );
  return setCurrentRecord;
}

/**
 * This hook is similar to react's useState hook, but uses the global
 * state of the given string attribute
 *
 * @example
 * ```
 * const [value, setValue] = useStringAttribute(viewAttribute);
 * ```
 * @param attribute the store attribute
 * @param row an optional row index
 * @returns [value, setValue, recordId]
 */
function useStringAttribute(attribute: string, row?: number) {
  const recordIndex = useContext(RowIndexContext) ?? undefined;
  const value = useAttribute<string>(attribute, '', row ?? recordIndex);
  return value;
}

/**
 * This hook is similar to react's useState hook, but uses the global
 * state of the given JSON attribute
 *
 * @example
 * ```
 * const [value, setValue] = useStringAttribute(viewAttribute);
 * ```
 * @param attribute the store attribute
 * @param row an optional row index
 * @returns [value, setValue, recordId]
 */
function useJSONAttribute(attribute: string, row?: number) {
  const recordIndex = useContext(RowIndexContext) ?? undefined;
  const value = useAttribute<unknown>(attribute, undefined, row ?? recordIndex);
  return value;
}

/**
 * This hook is similar to react's useState hook, but uses the global
 * state of the given number attribute
 *
 * @example
 * ```
 * const [value, setValue] = useNumberAttribute(viewAttribute);
 * ```
 * @param attribute the store attribute
 * @param row an optional row index
 * @returns [value, setValue, recordId]
 */
function useNumberAttribute(attribute: string, row?: number) {
  const recordIndex = useContext(RowIndexContext) ?? undefined;
  const value = useAttribute<number | undefined>(
    attribute,
    undefined,
    row ?? recordIndex,
  );
  return value;
}

/**
 * This hook is similar to react's useState hook, but uses the global
 * state of the given boolean attribute
 *
 * @example
 * ```
 * const [value, setValue] = useBooleanAttribute(viewAttribute);
 * ```
 * @param attribute the store attribute
 * @param row an optional row index
 * @returns [value, setValue, recordId]
 */
function useBooleanAttribute(attribute: string, row?: number) {
  const recordIndex = useContext(RowIndexContext) ?? undefined;
  const value = useAttribute<boolean>(attribute, false, row ?? recordIndex);
  return value;
}

/**
 * Use this hook if you want to rerender when the given attribute's dirty state changes
 * @param attribute The attribute to watch for
 * @returns true if dirty, false otherwise
 */
function useAttributeDirty(attribute: string, row?: number): boolean {
  const store = useContext(StoreContext);
  const id = useRecordId(row);
  return useStoreRecordAttributeDirty(store, id, attribute, row);
}

export function useStoreRecordAttributeDirty(
  store: Store<any> | null,
  id: string,
  attribute: string,
  row?: number,
): boolean {
  const attrPath = store?.attributePath ?? ['undefined'];
  const origAttrPath = store?.origAttributePath ?? ['undefined'];
  const recordStatusPath = store?.recordStatusPath ?? ['undefined'];
  const value = useStateXValue<unknown>(attrPath, undefined, {
    params: { id, attribute },
  });
  const origValue = useStateXValue<unknown>(origAttrPath, undefined, {
    params: { id, attribute },
  });
  const recordStatus = useStateXValue<RecordStatus>(recordStatusPath, 'N', {
    params: { id },
  });
  if (
    store?.hideDirtyIndicator ||
    row === Number.MAX_VALUE ||
    (row !== undefined && row < 0)
  ) {
    return false;
  }
  return (
    (!isEmpty(origValue) || recordStatus === 'I') &&
    !isNullEqual(value, origValue)
  );
}

/**
 * Use this hook to rerender whenever the selected records in the given store changes
 * @example
 * ```
 * const selectedRecordIds = useStoreSelectedRecordsOrder(myStore);
 * ```
 * @param store The store to watch for
 * @returns selectedRecordIds any array of record ids that are selected
 */
function useStoreSelectedRecordsOrder<S>(store: Store<S> | null): string[] {
  const ids = useStateXValue<Record<string, boolean>>(
    store?.selectedRecordIdsPath ?? ['dummy', 'selectedRecordsOrder'],
    EMPTY_OBJECT,
  );
  return useMemo(() => Object.keys(ids), [ids]);
}

/**
 * Use this hook to rerender whenever the selected records of the current store changes
 * @example
 * ```
 * const selectedRecordIds = useSelectedRecordsOrder();
 * ```
 * @returns selectedRecordIds any array of record ids that are selected
 */
function useSelectedRecordsOrder(): string[] {
  const store = useContext(StoreContext);
  return useStoreSelectedRecordsOrder(store);
}

/**
 * Use this hook to rerender whenever the records order in the given store changes
 * @example
 * ```
 * const recordIds = useStoreRecordsOrder(myStore);
 * ```
 * @param store The store to watch for
 * @returns recordIds in the order in which they are rendered
 */
function useStoreRecordsOrder<S>(store: Store<S> | null): string[] {
  return useStateXValue<string[]>(
    store?.recordsOrderPath ?? ['dummy', 'recordsOrder'],
    EMPTY_ARRAY as unknown as string[],
  );
}

/**
 * Use this hook to rerender whenever the records order in the parent store changes
 * @example
 * ```
 * const recordIds = useRecordsOrder();
 * ```
 * @returns recordIds in the order in which they are rendered
 */
function useRecordsOrder(): string[] {
  const store = useContext(StoreContext);
  return useStoreRecordsOrder(store);
}

/**
 * Use this hook to rerender whenever the totalRowCount in the parent store changes
 * @example
 * ```
 * const totalRowCount = useTotalRowCount();
 * ```
 * @returns total count of the table rows similar to count(1)
 */
function useTotalRowCount<S>(store: Store<S> | null): number | null {
  return useStateXValue<number | null>(
    store?.totalRowCountPath ?? ['dummy', 'totalRowCount'],
    null,
  );
}

const hasOtherSelectionSelector = selector<
  boolean,
  { selectedRecordIdsPath: Path; recordId: string }
>({
  path: ['transient', 'hasOtherSelection', ':pageId', ':itemId', ':rowIndex'],
  get: ({ get }, { selectedRecordIdsPath, recordId }) => {
    const selectedIds =
      get<Record<string, boolean>>(selectedRecordIdsPath) ?? {};
    const len = Object.keys(selectedIds).length;
    return len > 1 || (len === 1 && !selectedIds[recordId]);
  },
});

const isAllRowsSelectedSelector = selector<
  boolean,
  { selectedRecordIdsPath: Path; recordsOrderPath: Path }
>({
  path: ['transient', 'isMultiRowSelected', ':pageId', ':itemId'],
  get: ({ get }, { selectedRecordIdsPath, recordsOrderPath }) => {
    const selectedIds =
      get<Record<string, boolean>>(selectedRecordIdsPath) ?? {};
    const recordsOrder = get<string[]>(recordsOrderPath) ?? [];
    return (
      recordsOrder.length > 0 &&
      Object.keys(selectedIds).length > 0 &&
      Object.keys(selectedIds).length === recordsOrder.length
    );
  },
});

function useRowSelectionCount() {
  const store = useContext(StoreContext);
  const selectedRecords = useStateXValue(
    store?.selectedRecordIdsPath ?? ['dummy', 'selectedRecordIdsPath'],
    {},
  );
  return Object.keys(selectedRecords).length;
}

function useIsAllRowsSelected() {
  const store = useContext(StoreContext);
  const allSelected = useStateXSelector(isAllRowsSelectedSelector, {
    params: { pageId: store?.pageId ?? 'x', itemId: store?.itemId ?? 'x' },
    props: {
      selectedRecordIdsPath: store?.selectedRecordIdsPath ?? [
        'dummy',
        'selectedRecordIdsPath',
      ],
      recordsOrderPath: store?.recordsOrderPath ?? [
        'dummy',
        'recordsOrderPath',
      ],
    },
  });
  const hasSelection = useStateXSelector(hasOtherSelectionSelector, {
    params: {
      pageId: store?.pageId ?? 'x',
      itemId: store?.itemId ?? 'x',
      rowIndex: 'header',
    },
    props: {
      selectedRecordIdsPath: store?.selectedRecordIdsPath ?? [
        'dummy',
        'selectedRecordIdsPath',
      ],
      recordId: 'x',
    },
  });
  return { allSelected, hasSelection };
}

function useRecordSelection(row: number) {
  const store = useContext(StoreContext);
  const recordId = useRecordId(row);
  const selected = useStateXValue(
    [
      ...(store?.selectedRecordIdsPath || ['dummy', 'selectedRecordIdsPath']),
      recordId,
    ],
    false,
  );

  const hasOtherSelection = useStateXSelector(hasOtherSelectionSelector, {
    params: {
      pageId: store?.pageId ?? 'x',
      itemId: store?.itemId ?? 'x',
      rowIndex: `r-${row}`,
    },
    props: {
      selectedRecordIdsPath: store?.selectedRecordIdsPath ?? [
        'dummy',
        'selectedRecordIdsPath',
      ],
      recordId,
    },
  });

  const select = useCallback(() => {
    if (!store) return;
    store.selectRecord(recordId);
  }, [store, recordId]);

  const deSelect = useCallback(() => {
    if (!store) return;
    store.deSelectRecord(recordId);
  }, [store, recordId]);

  return {
    selected,
    select,
    deSelect,
    hasOtherSelection,
    recordId,
  };
}

export {
  StoreContext,
  useAttribute,
  useAttributeDefinition,
  useAttributeDirty,
  useAttributeSortState,
  useBooleanAttribute,
  useCurrentDataStore,
  useCurrentRecord,
  useDataStoreBusy,
  useDataStoreDirty,
  useIsAllRowsSelected,
  useIsCurrentRecordFromDB,
  useIsEditable,
  useIsRecordAtIndexCurrent,
  useIsRecordAtIndexDeleted,
  useIsRecordAtIndexFromDB,
  useIsRecordDeleted,
  useIsRecordFromDB,
  useJSONAttribute,
  useNumberAttribute,
  useRecordCount,
  useRecordId,
  useRecordSelection,
  useRecords,
  useRecordsFreq,
  useRecordsOrder,
  useRowSelectionCount,
  useSelectedRecordsOrder,
  useSetCurrentRecord,
  useStoreAttribute,
  useStoreCurrentRecordAttribute,
  useStoreRecordId,
  useStoreRecords,
  useStoreRecordsOrder,
  useStoreSelectedRecordsOrder,
  useStringAttribute,
  useTotalRowCount,
};

export default React.forwardRef(IODataStoreReference);
